import { AppWindow, Monitor } from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Image } from "@/components/ui/image";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ScreenSource } from "@/types/electron";

type ScreenSourcePickerProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	sources: ScreenSource[];
	onSelect: (source: ScreenSource) => void;
};

function SourceGrid({
	sources,
	onSelect,
	onOpenChange,
}: {
	sources: ScreenSource[];
	onSelect: (source: ScreenSource) => void;
	onOpenChange: (open: boolean) => void;
}) {
	if (sources.length === 0) {
		return (
			<div className="flex h-48 items-center justify-center text-muted-foreground">
				No sources available
			</div>
		);
	}

	return (
		<div className="grid max-h-80 grid-cols-2 gap-3 overflow-y-auto p-1">
			{sources.map((source) => (
				<button
					className="group flex flex-col items-center gap-2 rounded-lg border border-border p-3 transition-colors hover:border-primary hover:bg-accent"
					key={source.id}
					onClick={() => {
						onSelect(source);
						onOpenChange(false);
					}}
					type="button"
				>
					{source.thumbnailDataUrl ? (
						<Image
							alt={source.name}
							className="h-24 w-full rounded border border-border object-contain"
							height={96}
							src={source.thumbnailDataUrl}
							width={150}
						/>
					) : (
						<div className="flex h-24 w-full items-center justify-center rounded border border-border bg-muted">
							<Monitor className="size-8 text-muted-foreground" />
						</div>
					)}
					<span className="line-clamp-1 text-sm">{source.name}</span>
				</button>
			))}
		</div>
	);
}

export function ScreenSourcePicker({
	open,
	onOpenChange,
	sources,
	onSelect,
}: ScreenSourcePickerProps) {
	const screens = useMemo(
		() => sources.filter((s) => s.sourceType === "screen"),
		[sources]
	);
	const windows = useMemo(
		() => sources.filter((s) => s.sourceType === "window"),
		[sources]
	);

	return (
		<Dialog onOpenChange={onOpenChange} open={open}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>Choose what to share</DialogTitle>
					<DialogDescription>
						Select a screen or window to share
					</DialogDescription>
				</DialogHeader>
				<Tabs defaultValue="screens">
					<TabsList className="grid w-full grid-cols-2">
						<TabsTrigger className="gap-2" value="screens">
							<Monitor className="size-4" />
							Screens ({screens.length})
						</TabsTrigger>
						<TabsTrigger className="gap-2" value="windows">
							<AppWindow className="size-4" />
							Windows ({windows.length})
						</TabsTrigger>
					</TabsList>
					<TabsContent value="screens">
						<SourceGrid
							onOpenChange={onOpenChange}
							onSelect={onSelect}
							sources={screens}
						/>
					</TabsContent>
					<TabsContent value="windows">
						<SourceGrid
							onOpenChange={onOpenChange}
							onSelect={onSelect}
							sources={windows}
						/>
					</TabsContent>
				</Tabs>
				<div className="flex justify-end">
					<Button onClick={() => onOpenChange(false)} variant="outline">
						Cancel
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
